import ListenerManager from './listenerManager';
import MediaManager from './mediaManager';
import PlayerUtils from '../../../../../shared/utils/player';
import Shaka from 'shaka-player/dist/shaka-player.compiled';
import Youbora from '../../../../classes/youbora';
import displayError from '../../../../../shared/actions/player/displayError';
import { MediaPlayerProps as Props } from '../../../../../shared/types/props/player';
import { IPlayerError } from '../../../../../shared/types/interfaces/error';
import { adsSelector } from '@shared/store/slices/ads.slice';
import { mediaSelector } from '@shared/store/slices/media.slice';
import { routerSelector } from '@shared/store/slices/router.slice';
import { useAppSelector } from '@shared/store/hooks';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  PlayerError,
  PlayerType,
} from '../../../../../shared/types/enums/player';
import AnalyticsInstance from '@shared/utils/analytics';
window.muxjs = require('mux.js');

const ZAPPER_DELAY = 500;

export default function MediaPlayer({
  asset,
  channel,
  drm,
  headers,
  position,
  source,
}: Props) {
  const [loaded, setLoaded] = useState(false);
  const { pauseAd } = useAppSelector(adsSelector);
  const { startover: rawStartover } = useAppSelector(mediaSelector);
  const { params } = useAppSelector(routerSelector);
  const player = useRef<Shaka.Player | null>(null);
  const video = useRef<HTMLVideoElement | null>(null);
  const youbora = useRef<Youbora | null>(null);
  const autoplay = useMemo(() => !pauseAd, []);
  const startover = useMemo(
    () => rawStartover || params.current?.restart || false,
    [params, rawStartover]
  );
  const props = {
    shaka: player?.current,
    video: video?.current,
    source,
    ...position,
  };

  useEffect(() => {
    // Player and Youbora destruction
    return () => {
      player.current?.unload();
      player.current?.destroy();
      player.current = null;
      youbora.current?.destroy();
      youbora.current = null;
    };
  }, []);

  useEffect(() => {
    if (player.current) {
      youbora.current = new Youbora({
        asset,
        channel,
        player: { instance: player.current, type: PlayerType.SHAKA },
      });
      youbora.current?.start();
    }
    return () => {
      youbora.current?.destroy();
      youbora.current = null;
    };
  }, [startover]);

  useEffect(() => {
    // Initialize default error
    const error: IPlayerError = {
      player: PlayerType.SHAKA,
      type: PlayerError.LOAD,
    };

    let playerTimeout: ReturnType<typeof setTimeout> | undefined = undefined;

    try {
      playerTimeout = setTimeout(() => {
        // Player creation
        if (!player.current) {
          Shaka.polyfill.installAll();
          if (Shaka.Player.isBrowserSupported() && video.current) {
            player.current = new Shaka.Player(video.current);
          }
        }

        // Destroy previous youbora instance
        youbora.current?.destroy();
        youbora.current = null;

        // Required headers
        if (player.current && source) {
          player.current
            ?.getNetworkingEngine()
            ?.registerRequestFilter(function (type, request) {
              if (type == Shaka.net.NetworkingEngine.RequestType.LICENSE) {
                const {
                  accessToken,
                  contentType,
                  deviceId,
                  planId,
                  platform,
                  userId,
                } = headers || {};
                if (accessToken)
                  request.headers['x-access-token'] = accessToken;
                request.headers['content-type'] = contentType || '';
                request.headers['x-tvup-device'] = deviceId || '';
                request.headers['x-tvup-planid'] = planId || '';
                request.headers['x-tvup-platform'] = platform || '';
                request.headers['x-tvup-userid'] = userId || '';
              }
            });

          // Player configuration
          player.current?.configure({
            autoplay,
            abr: {
              defaultBandwidthEstimate: 500000,
              enabled: true,
              manager: Shaka.abr.SimpleAbrManager,
            },
            drm: drm
              ? {
                  advanced: {
                    'com.widevine.alpha': {
                      videoRobustness: 'SW_SECURE_CRYPTO',
                      audioRobustness: 'SW_SECURE_CRYPTO',
                      persistentStateRequired: !1,
                    },
                  },
                  servers: {
                    [drm.key]: drm.license,
                  },
                }
              : {},
            streaming: {
              rebufferingGoal: 15,
            },
          });

          // Player and Youbora initialization
          if (Shaka.net.HttpXHRPlugin) {
            Shaka.net.NetworkingEngine.registerScheme(
              'http',
              Shaka.net.HttpXHRPlugin.parse,
              Shaka.net.NetworkingEngine.PluginPriority.PREFERRED
            );
            Shaka.net.NetworkingEngine.registerScheme(
              'https',
              Shaka.net.HttpXHRPlugin.parse,
              Shaka.net.NetworkingEngine.PluginPriority.PREFERRED
            );
          }
          youbora.current = new Youbora({
            asset,
            channel,
            player: { instance: player.current, type: PlayerType.SHAKA },
          });
          if (video.current) {
            AnalyticsInstance.getOmnitrackManager()?.attachShakaPlayer(
              player.current,
              video.current
            );
          }

          player.current
            ?.load(
              source,
              position.keepWatching || position.bookmark || position.start
            )
            .then(() => {
              setLoaded(true);
            })
            .catch((e) => {
              // Just print errors not related to load interrupt
              // Fast user input will interrupt load (Error 7000)
              if (e?.code !== 7000) {
                error.code = e?.code || error.code;
                if (e.data.length > 3) {
                  const reasonHeaders = e.data[3];
                  const reason = reasonHeaders['x-tvup-reason'];
                  const geoBlockError = PlayerUtils.getGeoBlockError(
                    null,
                    false,
                    reason,
                    e.data[1]
                  );
                  if (geoBlockError) {
                    error.type = geoBlockError;
                  }
                }
                displayError(error);
              }
            });
        } else {
          const text = 'No source available';
          error.detail = text;
          console.error('[Shaka]', text);
          displayError(error);
        }
      }, ZAPPER_DELAY);
    } catch (e) {
      const text = 'Error loading player';
      error.detail = text;
      console.error('[Shaka]', text, e);
      displayError(error);
    }

    return () => {
      player.current?.unload();
      clearTimeout(playerTimeout);
      setLoaded(false);
    };
  }, [source]);

  return (
    <>
      <video
        autoPlay={autoplay}
        id={'video'}
        ref={video}
        height={'100%'}
        width={'100%'}
      />
      {loaded ? (
        <>
          <ListenerManager {...props} />
          <MediaManager {...props} />
        </>
      ) : null}
    </>
  );
}
